import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CommunityProvider, useCommunity, REPORT_STATUS } from './contexts/CommunityContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Layout
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import PendingUsers from './pages/PendingUsers';
import Reports from './pages/Reports';
import HiddenPosts from './pages/HiddenPosts';
import Services from './pages/Services';
import AiCost from './pages/AiCost';
import ApiDashboardPage from './pages/ApiDashboardPage';
import ApiKeys from './pages/ApiKeys';
import ServerMonitorPage from './pages/ServerMonitorPage';
import ProjectDetail from './pages/ProjectDetail';
import Projects from './pages/Projects';
import Monitoring from './pages/Monitoring';
import Deployments from './pages/Deployments';
import Alerts from './pages/Alerts';
import AuditLog from './pages/AuditLog';
import SecurityDashboard from './pages/SecurityDashboard';
import AiUsageTracker from './pages/AiUsageTracker';

// Admin Route Wrapper - checks for admin tier
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <span className="loading-text">인증 확인 중...</span>
      </div>
    );
  }

  if (!user || user.tier !== 'admin') {
    return (
      <div className="access-denied">
        <h2>접근 권한이 없습니다</h2>
        <p>관리자 계정으로 로그인해주세요.</p>
        <a href="https://ilouli.com/login" className="btn-primary">로그인</a>
      </div>
    );
  }

  return children;
};

// App Content with Badges
const AppContent = () => {
  const { getAllUsers, getPendingUsers, user } = useAuth();
  const { getReports, getHiddenPosts } = useCommunity();
  const [badges, setBadges] = useState({
    pendingUsers: 0,
    pendingReports: 0,
    hiddenPosts: 0
  });

  const loadBadges = useCallback(async () => {
    if (!user || user.tier !== 'admin') return;

    try {
      const [pending, reports, hidden] = await Promise.all([
        getPendingUsers(),
        getReports(),
        getHiddenPosts()
      ]);

      const pendingReports = reports?.filter(r => r.status === REPORT_STATUS.PENDING) || [];

      setBadges({
        pendingUsers: pending?.length || 0,
        pendingReports: pendingReports.length,
        hiddenPosts: hidden?.length || 0
      });
    } catch (err) {
      console.error('Failed to load badges:', err);
    }
  }, [user, getPendingUsers, getReports, getHiddenPosts]);

  useEffect(() => {
    loadBadges();
    // 1분마다 뱃지 갱신
    const interval = setInterval(loadBadges, 60000);
    return () => clearInterval(interval);
  }, [loadBadges]);

  return (
    <Routes>
      <Route path="/" element={
        <AdminRoute>
          <Layout badges={badges} />
        </AdminRoute>
      }>
        {/* Dashboard */}
        <Route index element={<Dashboard />} />

        {/* Users */}
        <Route path="users" element={<Users />} />
        <Route path="users/pending" element={<PendingUsers />} />

        {/* Moderation */}
        <Route path="reports" element={<Reports />} />
        <Route path="reports/hidden" element={<HiddenPosts />} />

        {/* Services */}
        <Route path="services" element={<Services />} />

        {/* AI */}
        <Route path="ai" element={<AiCost />} />

        {/* API */}
        <Route path="api-dashboard" element={<ApiDashboardPage />} />
        <Route path="api-keys" element={<ApiKeys />} />

        {/* Server */}
        <Route path="server/monitor" element={<ServerMonitorPage />} />
        <Route path="server/database" element={<ServerMonitorPage />} />

        {/* Monitoring (Phase 4) */}
        <Route path="monitoring" element={<Monitoring />} />

        {/* Deployments (Phase 3) */}
        <Route path="deployments" element={<Deployments />} />

        {/* Alerts (Phase 5) */}
        <Route path="alerts" element={<Alerts />} />

        {/* Audit Log (Phase 6) */}
        <Route path="audit-log" element={<AuditLog />} />

        {/* Security Dashboard (Phase 6) */}
        <Route path="security" element={<SecurityDashboard />} />

        {/* AI Usage Tracking */}
        <Route path="ai-usage" element={<AiUsageTracker />} />
        <Route path="ai-usage/features" element={<AiUsageTracker initialTab="features" />} />

        {/* Projects */}
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectName" element={<ProjectDetail />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CommunityProvider>
          <NotificationProvider>
            <Router>
              <AppContent />
            </Router>
          </NotificationProvider>
        </CommunityProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
